import React, { memo, ReactNode } from 'react';

import { addRootNodeClassName, cn } from '../../lib/css/className/className';
import { TextProps } from '../TextGeneric/TextGeneric';
import { VStack, HStack, Stack } from '../Stack';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { TypographyTextPresetWithSkin, useTypographyWithSkin } from '../../hooks/useTypography/useTypographyWithSkin';

import { HeaderLevel } from './types';
import { headerAddonSizes, headerSkinSizes } from './utils';
import './Header.css';

const cls = cn('Header');

export interface HeaderProps extends Omit<TextProps, 'textSize' | 'role' | 'as' | 'bold'> {
    /**
     * Уровень заголовка
     */
    level: HeaderLevel;

    /**
     * Текстовый аддон справа от заголовков уровней h2-h5.
     * Использует отличное от `addonRight` выравнивание специально для текста.
     * Размер и цвет аддона задаётся в соответствии с размером заголовка и определён дизайн-системой.
     */
    textAddonRight?: ReactNode;

    /**
     * Аддон справа рядом с текстом заголовка
     * Размер аддона задаётся в соответствии с размером текста и определён дизайн-системой.
     */
    headerAddonRight?: ReactNode;
}

/**
 * Компонент для создания заголовков уровней h1-h5
 */
export const Header = memo<HeaderProps>(function Header({
    className,
    level,
    children,
    caption,
    color = 'gray',
    textAddonRight,
    addonRight,
    headerAddonRight,
    ...props
}) {
    const skinSize = headerSkinSizes[level];
    const sizeSkinClass = getSizeSkin(skinSize);
    const textSize: TypographyTextPresetWithSkin = 'current-main + current-body';

    const addonSizes = headerAddonSizes[level];
    const { captionClasses: verticalAlignCaptionClasses, addonClasses } = useInternalVerticalAlignWithSkin({
        textSize,
        addonType: 'universal',
    });
    const { captionClasses: typographyCaptionClasses } = useTypographyWithSkin({
        size: textSize,
        color,
    });

    let content = (
        <TextGenericWithSkin
            {...props}
            className={cls('header')}
            skinSize={skinSize}
            textSize={textSize}
            caption={caption}
            color={color}
            as={level}
            bold
        >
            {children}
        </TextGenericWithSkin>
    );

    // поскольку поведение addonRight для заголовка другое - требуется обернуть всё
    // в отдельный контейнер, а также отделить заголовок от caption.
    // для addonLeft используется стандартное поведение Text
    if (textAddonRight || addonRight || headerAddonRight) {
        content = (
            <VStack>
                <HStack gap={'8'} className={cls('addonContainer')}>
                    <TextGenericWithSkin
                        {...props}
                        addonRight={headerAddonRight}
                        className={cls('header')}
                        skinSize={skinSize}
                        textSize={textSize}
                        caption={undefined}
                        color={color}
                        as={level}
                        bold
                    >
                        {children}
                    </TextGenericWithSkin>

                    {addonRight && <Stack className={cls('addonRight', [addonClasses])}>{addonRight}</Stack>}

                    {textAddonRight && (
                        <TextGenericWithSkin
                            skinSize={skinSize}
                            textSize={'current-body'}
                            color="gray"
                            role="supplementary"
                            className={cls('textAddonRight', {
                                padding: addonSizes.textAddonPadding,
                            })}
                        >
                            {textAddonRight}
                        </TextGenericWithSkin>
                    )}
                </HStack>
                {Boolean(caption) && (
                    <span className={cls('caption', [typographyCaptionClasses, verticalAlignCaptionClasses])}>
                        {caption}
                    </span>
                )}
            </VStack>
        );
    }

    return addRootNodeClassName(
        content,
        cls({ withTextAddonRight: Boolean(textAddonRight) }, [sizeSkinClass, className]),
    );
});
